import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Printer, ArrowLeft, Loader2 } from 'lucide-react';
import { Button } from '../components/ui/button';

const LOGO_URL = '/images/nice-logo.jpg';
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PrintPackingSlipPage = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated, isLoading: authLoading } = useAuth();
  
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submission, setSubmission] = useState(null);
  const [cards, setCards] = useState([]);
  const [shippingAddress, setShippingAddress] = useState(null);

  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      navigate('/login', { state: { from: { pathname: `/submissions/${submissionId}/print` } } });
      return;
    }

    if (isAuthenticated && submissionId) {
      fetchSubmissionData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, isAuthenticated, submissionId, navigate]);

  const fetchSubmissionData = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await axios.get(`${API}/submissions/${submissionId}/print`);
      const data = response.data;
      
      setSubmission(data);
      setCards(data.cards || []);
      setShippingAddress(data.shipping_address);
    } catch (err) {
      console.error('Failed to fetch submission:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to view this packing slip.');
      } else if (err.response?.status === 404) {
        setError('Submission not found.');
      } else {
        setError('Failed to load submission data. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const formatDate = (dateString) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch (e) {
      return new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    }
  };

  if (authLoading || isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center">
          <Loader2 className="w-12 h-12 animate-spin text-[#003594] mx-auto mb-4" />
          <p className="text-gray-600">Loading packing slip...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="text-center max-w-md mx-auto px-4">
          <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <span className="text-2xl">⚠️</span>
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Unable to Load</h1>
          <p className="text-gray-600 mb-6">{error}</p>
          <Link to="/dashboard">
            <Button className="bg-[#003594] hover:bg-[#002554]">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  // Use dynamic values from database, calculate shipping if missing
  const cardCount = submission?.card_count || 0;
  const pricePerCard = submission?.price_per_card || 0;
  const gradingCost = submission?.grading_cost || (cardCount * pricePerCard);
  
  // Calculate shipping based on card count if not stored (1.6 oz per card, $7 for ≤9oz)
  const calculateShipping = (count) => {
    const weightOz = count * 1.6;
    if (weightOz <= 9) return 7;
    const weightLbs = Math.ceil(weightOz / 16);
    if (weightLbs <= 1) return 9;
    if (weightLbs <= 3) return 10;
    if (weightLbs <= 5) return 13;
    if (weightLbs <= 8) return 15;
    if (weightLbs <= 11) return 16;
    if (weightLbs <= 16) return 19;
    if (weightLbs <= 20) return 20;
    return 20 + Math.ceil((weightLbs - 20) / 5) * 2;
  };
  
  const shippingCost = submission?.shipping_cost ?? calculateShipping(cardCount);
  const insuranceCost = submission?.insurance_cost ?? 0;
  const packageWeight = submission?.package_weight_lbs || Math.ceil((cardCount * 1.6) / 16);
  const voucherDiscount = submission?.voucher_discount || 0;
  
  // Calculate correct total: grading + shipping + insurance - voucher discount
  const calculatedTotal = Math.max(0, gradingCost + shippingCost + insuranceCost - voucherDiscount);
  const orderTotal = submission?.total_price ?? calculatedTotal;
  const estimatedValue = submission?.estimated_submission_value || 0;

  // Format currency with commas
  const formatCurrency = (val) => {
    if (val === 0) return '$0';
    return `$${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          .no-print { display: none !important; }
          body { margin: 0; padding: 0; }
          .print-container { 
            padding: 20px !important;
            max-width: 100% !important;
          }
          .print-header { page-break-after: avoid; }
          .print-section { page-break-inside: avoid; }
          * { 
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }
        }
        @media screen {
          .print-container {
            max-width: 800px;
            margin: 0 auto;
            padding: 20px;
          }
        }
      `}</style>

      {/* Print Action Bar - Hidden when printing */}
      <div className="no-print bg-gray-100 border-b border-gray-200 py-4 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 flex items-center justify-between">
          <Link to="/dashboard" className="text-gray-600 hover:text-gray-900 flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" />
            Back to Dashboard
          </Link>
          <Button 
            onClick={handlePrint}
            className="bg-[#003594] hover:bg-[#002554] flex items-center gap-2"
          >
            <Printer className="w-4 h-4" />
            Print This Page
          </Button>
        </div>
      </div>

      {/* Printable Content */}
      <div className="print-container bg-white min-h-screen">
        {/* Header */}
        <div className="print-header border-b-2 border-[#003594] pb-4 mb-6">
          <div className="flex justify-between items-start">
            <div>
              <img 
                src={LOGO_URL} 
                alt="NICE Grading" 
                className="h-16 w-auto mb-2"
                crossOrigin="anonymous"
              />
            </div>
            <div className="text-right">
              <h1 className="text-xl font-bold text-[#003594]">CARD GRADING SUBMISSION</h1>
              <p className="text-gray-500 text-sm">Include this form with your cards</p>
            </div>
          </div>
        </div>

        {/* Submission Number Box */}
        <div className="print-section bg-gray-100 border-2 border-[#003594] rounded-lg p-4 mb-6">
          <div className="flex justify-between items-center">
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide">Submission Number</p>
              <p className="text-2xl font-bold text-[#003594]">{submission?.submission_number || 'N/A'}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500 uppercase tracking-wide">Date Submitted</p>
              <p className="text-lg font-semibold">{formatDate(submission?.created_at)}</p>
            </div>
          </div>
        </div>

        {/* Customer Info */}
        <div className="print-section grid grid-cols-2 gap-6 mb-6 pb-6 border-b border-gray-200">
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Customer Name</p>
            <p className="font-semibold text-gray-900">{user?.name || 'N/A'}</p>
          </div>
          <div>
            <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Customer Email</p>
            <p className="font-semibold text-gray-900">{user?.email || 'N/A'}</p>
          </div>
          {user?.phone && (
            <div>
              <p className="text-xs text-gray-500 uppercase tracking-wide mb-1">Phone</p>
              <p className="font-semibold text-gray-900">{user.phone}</p>
            </div>
          )}
        </div>

        {/* Return Address */}
        <div className="print-section mb-6 pb-6 border-b border-gray-200">
          <h2 className="text-sm font-bold text-[#003594] uppercase tracking-wide mb-3">Return Shipping Address</h2>
          {shippingAddress ? (
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="font-semibold">{shippingAddress.name}</p>
              <p className="text-gray-700">{shippingAddress.street_address}</p>
              <p className="text-gray-700">
                {shippingAddress.city}, {shippingAddress.state} {shippingAddress.zip_code}
              </p>
              {shippingAddress.phone && (
                <p className="text-gray-600 text-sm mt-1">Phone: {shippingAddress.phone}</p>
              )}
            </div>
          ) : (
            <p className="text-gray-500 italic">No return address provided</p>
          )}
        </div>

        {/* Submission Summary */}
        <div className="print-section mb-6 pb-6 border-b border-gray-200">
          <h2 className="text-sm font-bold text-[#003594] uppercase tracking-wide mb-3">Submission Summary</h2>
          <div className="space-y-2">
            <div className="flex justify-between">
              <span className="text-gray-600">Total Cards:</span>
              <span className="font-medium">{cardCount} cards</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Price Per Card:</span>
              <span className="font-medium">{formatCurrency(pricePerCard)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Grading Total:</span>
              <span className="font-medium">{formatCurrency(gradingCost)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Return Shipping:</span>
              <span className="font-medium">{formatCurrency(shippingCost)}</span>
            </div>
            {packageWeight > 0 && (
              <div className="flex justify-between pl-4">
                <span className="text-gray-500 text-sm">Package weight:</span>
                <span className="text-gray-500 text-sm">{packageWeight} lbs</span>
              </div>
            )}
            {insuranceCost > 0 ? (
              <>
                <div className="flex justify-between">
                  <span className="text-gray-600">Insurance:</span>
                  <span className="font-medium">{formatCurrency(insuranceCost)}</span>
                </div>
                {estimatedValue > 0 && (
                  <div className="flex justify-between pl-4">
                    <span className="text-gray-500 text-sm">Coverage up to:</span>
                    <span className="text-gray-500 text-sm">${Number(estimatedValue).toLocaleString()}</span>
                  </div>
                )}
              </>
            ) : estimatedValue > 0 && estimatedValue <= 100 ? (
              <div className="flex justify-between">
                <span className="text-gray-600">Insurance:</span>
                <span className="font-medium">Included</span>
              </div>
            ) : null}
            {voucherDiscount > 0 && (
              <div className="flex justify-between text-green-700">
                <span>Discount Applied:</span>
                <span className="font-medium">-{formatCurrency(voucherDiscount)}</span>
              </div>
            )}
            <div className="flex justify-between pt-2 border-t border-gray-300">
              <span className="font-bold text-[#003594]">Order Total:</span>
              <span className="font-bold text-[#003594]">{formatCurrency(orderTotal)}</span>
            </div>
            {estimatedValue > 0 && (
              <div className="flex justify-between pt-2 border-t border-gray-200">
                <span className="text-gray-600">Declared Value:</span>
                <span className="font-medium">${Number(estimatedValue).toLocaleString()}</span>
              </div>
            )}
          </div>
        </div>

        {/* Card Details */}
        <div className="print-section mb-6 pb-6 border-b border-gray-200">
          <h2 className="text-sm font-bold text-[#003594] uppercase tracking-wide mb-3">Card Details</h2>
          {cards && cards.length > 0 ? (
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-[#003594] text-white">
                  <th className="border border-[#003594] px-3 py-2 text-left text-sm">#</th>
                  <th className="border border-[#003594] px-3 py-2 text-left text-sm">Year</th>
                  <th className="border border-[#003594] px-3 py-2 text-left text-sm">Set/Brand</th>
                  <th className="border border-[#003594] px-3 py-2 text-left text-sm">Card #</th>
                  <th className="border border-[#003594] px-3 py-2 text-left text-sm">Player/Subject</th>
                </tr>
              </thead>
              <tbody>
                {cards.map((card, index) => (
                  <tr key={card.id || index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                    <td className="border border-gray-300 px-3 py-2 text-sm">{index + 1}</td>
                    <td className="border border-gray-300 px-3 py-2 text-sm">{card.year || '-'}</td>
                    <td className="border border-gray-300 px-3 py-2 text-sm">{card.set_brand || '-'}</td>
                    <td className="border border-gray-300 px-3 py-2 text-sm">{card.card_number || '-'}</td>
                    <td className="border border-gray-300 px-3 py-2 text-sm">{card.player_name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="bg-gray-100 rounded-lg p-4 text-center">
              <p className="text-gray-600 italic">Card details will be researched upon receipt</p>
            </div>
          )}
        </div>

        {/* Shipping Addresses */}
        <div className="print-section bg-gray-50 border-2 border-[#003594] rounded-lg p-6 mb-6">
          <h2 className="text-lg font-bold text-[#003594] mb-4 flex items-center gap-2">
            📦 SHIP YOUR CARDS TO:
          </h2>
          <p className="text-sm text-gray-600 mb-4">Use the address that matches your shipping carrier. Include this packing slip inside your package.</p>
          
          <div className="grid grid-cols-2 gap-4">
            {/* USPS Address */}
            <div className="bg-blue-100 border-2 border-blue-400 rounded-lg p-4">
              <p className="font-bold text-blue-800 mb-2">📮 USPS</p>
              <div className="text-gray-900 text-sm">
                <p className="font-semibold">NICE Grading</p>
                <p>Attn: Receiving</p>
                <p>P.O. Box 30041</p>
                <p>Rochester, NY 14603</p>
              </div>
            </div>
            
            {/* UPS/FedEx Address */}
            <div className="bg-amber-100 border-2 border-amber-400 rounded-lg p-4">
              <p className="font-bold text-amber-800 mb-2">🚚 UPS / FedEx</p>
              <div className="text-gray-900 text-sm">
                <p className="font-semibold">NICE Grading</p>
                <p>Attn: Receiving</p>
                <p>250 Cumberland St.</p>
                <p>Ste. 202</p>
                <p>Rochester, NY 14605</p>
              </div>
            </div>
          </div>
          
          <p className="font-bold text-[#003594] mt-4 text-center text-lg">
            Reference: Submission #{submission?.submission_number}
          </p>
        </div>

        {/* Footer */}
        <div className="print-section text-center border-t border-gray-200 pt-6 mt-6">
          <p className="text-gray-600 text-sm">Questions? Contact us at support@nicegrading.com</p>
          <p className="text-gray-600 text-sm">Track your submission at nicegrading.com/dashboard</p>
          <p className="text-gray-400 text-xs mt-4">© {new Date().getFullYear()} NICE Grading. All rights reserved.</p>
        </div>
      </div>

      {/* Back to Confirmation - Hidden when printing */}
      <div className="no-print bg-gray-100 py-6">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-gray-600 mb-4">After printing, include this form with your cards.</p>
          <div className="flex gap-4 justify-center">
            <Button 
              onClick={handlePrint}
              className="bg-[#003594] hover:bg-[#002554]"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print Again
            </Button>
            <Link to="/dashboard">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Dashboard
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </>
  );
};

export default PrintPackingSlipPage;
